import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { BirthdayFundCertificateDesigner, type CertificateDesignerData } from "./BirthdayFundCertificateDesigner";
import { useExportImage } from "./useExportImage";
import { 
  useGetCertificateTemplate, 
  useCreateCertificateTemplate, 
  useUpdateCertificateTemplate 
} from "@/lib/api/birthdayFundCertificates";
import { GenerateLoadingOrError } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";

export function BirthdayFundCreateCertificate() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const templateId = searchParams.get("templateId");
  const { exportToPng, exportToJpeg, isExporting, error } = useExportImage();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [initialData, setInitialData] = useState<Partial<CertificateDesignerData> | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  const templateRes = useGetCertificateTemplate(templateId);
  const createTemplate = useCreateCertificateTemplate();
  const updateTemplate = useUpdateCertificateTemplate();
  
  const isUpdate = !!templateId;
  
  useEffect(() => {
    if (templateId && templateRes.data?.success) {
      const template = templateRes.data.data.template;
      setInitialData(template.template_data as Partial<CertificateDesignerData>);
    } else if (!templateId) {
      // No template ID, start with empty data
      setInitialData({});
    }
  }, [templateId, templateRes.data]);

  const loadingOrErrorUi = templateId ? GenerateLoadingOrError([templateRes]) : null;

  if (loadingOrErrorUi) {
    return (
      <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
        <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
          Create Certificate
        </h1>
        {loadingOrErrorUi}
      </section>
    );
  }

  // Don't render designer until we have initial data
  if (initialData === null) {
    return null;
  }

  const handleSave = async (data: CertificateDesignerData) => {
    setSaveError(null);
    setIsSaving(true);
    
    try {
      let result;
      
      if (isUpdate && templateId) {
        // Update existing template
        result = await updateTemplate(templateId, {
          location_name: data.locationName,
          template_data: data,
          certificate_type: "birthday_fund" as any,
        });
      } else {
        // Create new template
        result = await createTemplate({
          location_name: data.locationName,
          template_data: data,
          certificate_type: "birthday_fund" as any,
        });
      }
      
      if (!result.success) {
        throw result;
      }
      
      toast({
        title: isUpdate ? "Template Updated" : "Template Created",
        description: isUpdate 
          ? "Certificate template has been updated successfully." 
          : "Certificate template has been created successfully.",
      });
      
      // Navigate to dashboard after successful create
      if (!isUpdate) {
        navigate("/fund/birthday/certificates");
      }
      
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to save template";
      setSaveError(errorMessage);
      console.error("Save error:", err);
      
      toast({
        title: isUpdate ? "Failed to Update Template" : "Failed to Create Template",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportPng = (data: CertificateDesignerData, previewElement: HTMLDivElement) => {
    const filename = `birthday-beacon-certificate-${data.certificateNumber.replace("#", "")}.png`;
    exportToPng(previewElement, filename, {
      scale: 2,
      backgroundColor: "#ffffff",
    });
  };

  const handleExportJpeg = (data: CertificateDesignerData, previewElement: HTMLDivElement) => {
    const filename = `birthday-beacon-certificate-${data.certificateNumber.replace("#", "")}.jpg`;
    exportToJpeg(previewElement, filename, {
      scale: 2,
      backgroundColor: "#ffffff",
      quality: 0.95,
    });
  };

  return (
    <div>
      {error && (
        <div className="mx-6 mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
          Export failed: {error.message}
        </div>
      )}
      {saveError && (
        <div className="mx-6 mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
          Save failed: {saveError}
        </div>
      )}
      
      <BirthdayFundCertificateDesigner
        initialData={initialData}
        onSave={handleSave}
        onExportPng={handleExportPng}
        onExportJpeg={handleExportJpeg}
        isProcessing={isExporting || isSaving}
        isUpdate={isUpdate}
      />
    </div>
  );
}

export default BirthdayFundCreateCertificate;
